// Generic Firestore CRUD wrappers. Take a CollectionReference (from
// database.js helpers like jobsCol(), customersCol(), etc.) and operate
// on it. Phase 1 feature code calls these instead of the raw SDK so
// every doc gets consistent timestamps and a schemaVersion.
import {
  doc,
  addDoc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { allocateJobNumberSafe } from './job-number.js';
import { declinePendingRecommendations } from './recommendations.js';
import { customersCol, jobsCol, vehiclesCol } from '../database.js';
import { purgeJobsAssets } from './job-assets.js';

export const SCHEMA_VERSION = 1;

function newStamps() {
  return {
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    schemaVersion: SCHEMA_VERSION,
  };
}

function updateStamp() {
  return { updatedAt: serverTimestamp() };
}

export async function createDoc(colRef, data) {
  const ref = await addDoc(colRef, { ...data, ...newStamps() });
  return ref.id;
}

export async function setDocAt(colRef, id, data) {
  await setDoc(doc(colRef, id), { ...data, ...newStamps() });
}

export async function getOne(colRef, id) {
  const snap = await getDoc(doc(colRef, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function listAll(colRef, ...constraints) {
  const ref = constraints.length ? query(colRef, ...constraints) : colRef;
  const snap = await getDocs(ref);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function updateOne(colRef, id, patch) {
  await updateDoc(doc(colRef, id), { ...patch, ...updateStamp() });
}

export async function deleteOne(colRef, id) {
  await deleteDoc(doc(colRef, id));
}

// Create a job, stamping it with the next sequential job number for
// this mechanic. Every job-creation path goes through here so a
// ticket can never exist without an identity — see lib/job-number.js
// for why allocation is best-effort rather than fatal.
export async function createJobDoc(colRef, data) {
  const jobNumber = await allocateJobNumberSafe();
  return createDoc(colRef, { ...data, jobNumber });
}

// Cancel a job. Terminal, but not destructive: the job keeps its
// NUMBER, its inspection, and its photos.
//
// Deleting was the only option before, which meant losing an
// inspection you'd already done and punching an unexplained hole in
// the invoice sequence — EST-1042 vanishing with 1041 and 1043 still
// there is exactly what an accountant asks about. A cancelled job
// still exists and still says why.
export async function cancelJob(colRef, job, { reason, note = '', by = 'mechanic' }) {
  await updateOne(colRef, job.id, {
    status: 'cancelled',
    cancelledAt: serverTimestamp(),
    cancelReason: reason || 'other',
    cancelNote: (note || '').trim(),
    cancelledBy: by,
    // Stop it appearing as in-progress or awaiting anything.
    startedAt: null,
    awaitingReturn: false,
    awaitingPart: false,
    // Whatever they never answered is a no now.
    recommendations: declinePendingRecommendations(job.recommendations),
  });
}

// Put a cancelled job back. Returns it to the last status its
// paperwork supports rather than guessing: an invoice was sent →
// invoiced, an estimate was sent → sent, otherwise draft.
export async function uncancelJob(colRef, job) {
  const restored = job.invoiceShareId ? 'invoiced' : (job.shareId ? 'sent' : 'draft');
  await updateOne(colRef, job.id, {
    status: restored,
    cancelledAt: null,
    cancelReason: null,
    cancelNote: '',
    cancelledBy: null,
  });
  return restored;
}

// Archive a customer: gone from everywhere you work, but recoverable.
//
// Replaces a cascade delete that removed the customer, every vehicle
// and every job permanently behind a single confirm. That was one
// mis-tap away from destroying years of history with no way back —
// the worst kind of support call, and unanswerable once someone is
// paying for the app.
export async function archiveCustomer(colRef, customer, { reason = '' } = {}) {
  await updateOne(colRef, customer.id, {
    archivedAt: serverTimestamp(),
    archiveReason: (reason || '').trim(),
  });
}

// Permanent deletion. Moved here from the customer page so it can
// only be reached from the archive — the point of archiving is that
// destroying a customer becomes a deliberate second act rather than
// something one confirm dialog away from an ordinary tap.
//
// Storage is cleared BEFORE the documents, because once the job docs
// are gone nothing points at the files and they're orphaned beyond
// recovery.
export async function purgeCustomerForever(customerId) {
  const [jobsSnap, vehSnap] = await Promise.all([
    getDocs(query(jobsCol(),     where('customerId', '==', customerId))),
    getDocs(query(vehiclesCol(), where('customerId', '==', customerId))),
  ]);

  await purgeJobsAssets(jobsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

  const refs = [
    ...jobsSnap.docs.map((d) => d.ref),
    ...vehSnap.docs.map((d) => d.ref),
    doc(customersCol(), customerId),
  ];
  // Firestore caps a batch at 500 operations.
  while (refs.length > 0) {
    const batch = writeBatch(db);
    refs.splice(0, 400).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
  return { jobs: jobsSnap.size, vehicles: vehSnap.size };
}

export async function restoreCustomer(colRef, customer) {
  await updateOne(colRef, customer.id, {
    archivedAt: null,
    archiveReason: '',
  });
}

// Mark a job paid and close it. Also syncs the linked sharedInvoices doc
// when one exists so the customer-facing invoice page reflects payment.
export async function markJobAsPaid(colRef, job, closedBy = 'mark-paid') {
  await updateOne(colRef, job.id, {
    status: 'paid',
    paidAt: serverTimestamp(),
    closedAt: job.closedAt || serverTimestamp(),
    closedBy,
    awaitingReturn: false,
    awaitingPart: false,
  });
  if (job.invoiceShareId) {
    await updateDoc(doc(db, 'sharedInvoices', job.invoiceShareId), {
      paid: true,
      paidAt: serverTimestamp(),
    });
  }
}
